import { useEffect, useState } from "react";
import {blogHeroImage} from "../../../assets";

const HERO_CACHE_STORAGE_KEY = "blog_hero_daily_cache_v1";
const VIEW_COUNT_MIN = 1700;
const VIEW_COUNT_MAX = 4900;

const fallbackFeaturedPost = {
  title: "Strategi Lengkap Meningkatkan Skor IELTS dengan Metode Belajar Terstruktur",
  readTime: "5 min baca",
  href: "/blog",
  image: blogHeroImage,
};

const normalizeWordPressBaseUrl = (value = "") => {
  const trimmed = value.trim().replace(/\/$/, "");

  if (!trimmed) {
    return "";
  }

  if (trimmed.includes("/wp-json")) {
    return trimmed;
  }

  return `${trimmed}/wp-json/wp/v2`;
};

const readJsonCache = (storageKey, fallbackValue) => {
  if (typeof window === "undefined") {
    return fallbackValue;
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);

    if (!rawValue) {
      return fallbackValue;
    }

    return JSON.parse(rawValue);
  } catch {
    return fallbackValue;
  }
};

const writeJsonCache = (storageKey, value) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // Ignore storage quota or private mode failures.
  }
};

const stripHtml = (value = "") =>
  String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&#8217;/g, "'")
    .replace(/&#8220;/g, '"')
    .replace(/&#8221;/g, '"')
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();

const getJakartaDateKey = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Jakarta",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const year = parts.find((part) => part.type === "year")?.value || "";
  const month = parts.find((part) => part.type === "month")?.value || "";
  const day = parts.find((part) => part.type === "day")?.value || "";

  return `${year}-${month}-${day}`;
};

const formatManualViewCount = (value) => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1).replace(/\.0$/, "")}k views`;
  }

  return `${value} views`;
};

const getManualViewCount = (seed = "") => {
  const rangeSize = VIEW_COUNT_MAX - VIEW_COUNT_MIN + 1;
  const baseValue = hashString(`${getJakartaDateKey()}:${seed}`);
  const count = VIEW_COUNT_MIN + (baseValue % rangeSize);

  return formatManualViewCount(count);
};

const hashString = (value = "") =>
  Array.from(value).reduce((accumulator, character) => accumulator + character.charCodeAt(0), 0);

const isFeaturedTerm = (term) => {
  const normalizedValue = stripHtml(term?.slug || term?.name || "").toLowerCase();

  return normalizedValue === "featured" || normalizedValue.includes("featured");
};

const isHeroEligiblePost = (post) => {
  const terms = Array.isArray(post?._embedded?.["wp:term"]) ? post._embedded["wp:term"] : [];

  return !terms.flat().some(isFeaturedTerm);
};

const selectDailyPost = (posts, salt = "hero-section") => {
  if (!Array.isArray(posts) || posts.length === 0) {
    return null;
  }

  const index = hashString(`${getJakartaDateKey()}:${salt}`) % posts.length;

  return posts[index];
};

const estimateReadTimeFromContent = (content = "") => {
  const wordCount = stripHtml(content)
    .split(/\s+/)
    .filter(Boolean).length;

  return `${Math.max(1, Math.ceil(wordCount / 200))} min baca`;
};

const normalizeReadTimeLabel = (value = "") => {
  const cleanedValue = stripHtml(value);

  if (!cleanedValue) {
    return "";
  }

  if (/baca/i.test(cleanedValue)) {
    return cleanedValue.replace(/\s+/g, " ");
  }

  const minuteMatch = cleanedValue.match(/(\d+)\s*(menit|minutes?|min)\b/i);

  if (minuteMatch) {
    return `${minuteMatch[1]} min baca`;
  }

  return `${cleanedValue} baca`;
};

const getWordPressReadTime = (post) => {
  const yoastReadTime = stripHtml(post?.yoast_head_json?.twitter_misc?.["Estimasi waktu membaca"] || "");

  if (yoastReadTime) {
    return normalizeReadTimeLabel(yoastReadTime);
  }

  return estimateReadTimeFromContent(post?.content?.rendered || "");
};

const readHeroCache = () => {
  const cachedValue = readJsonCache(HERO_CACHE_STORAGE_KEY, null);
  const todayKey = getJakartaDateKey();

  if (!cachedValue || cachedValue.cacheDate !== todayKey || !cachedValue.article) {
    return null;
  }

  const repairedArticle = {
    ...cachedValue.article,
    viewCount: cachedValue.article.viewCount || getManualViewCount(cachedValue.article.href || cachedValue.article.title || "cached-hero"),
  };

  if (repairedArticle.viewCount !== cachedValue.article.viewCount) {
    writeJsonCache(HERO_CACHE_STORAGE_KEY, {
      cacheDate: todayKey,
      article: repairedArticle,
    });
  }

  return repairedArticle;
};

const writeHeroCache = (article) => {
  writeJsonCache(HERO_CACHE_STORAGE_KEY, {
    cacheDate: getJakartaDateKey(),
    article,
  });
};

const mapHeroPost = (post) => ({
  image: post?._embedded?.["wp:featuredmedia"]?.[0]?.source_url || blogHeroImage,
  title: stripHtml(post?.title?.rendered || "Untitled"),
  readTime: getWordPressReadTime(post),
  viewCount: getManualViewCount(post?.id || post?.slug || post?.link || "hero"),
  href: post?.link || "/blog",
});

const scrollToSection = (sectionId) => {
  const targetElement = document.getElementById(sectionId);

  if (targetElement) {
    targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const getFallbackHeroArticle = () => ({
  ...fallbackFeaturedPost,
  viewCount: getManualViewCount("fallback"),
});

const HeroBlog = () => {
  const [cachedHeroArticle] = useState(() => readHeroCache());
  const [heroArticle, setHeroArticle] = useState(() => cachedHeroArticle || getFallbackHeroArticle());
  const [isLoading, setIsLoading] = useState(() => !cachedHeroArticle);

  useEffect(() => {
    if (cachedHeroArticle) {
      return undefined;
    }

    const wordpressBaseUrl = normalizeWordPressBaseUrl(process.env.REACT_APP_WORDPRESS_API_URL);

    if (!wordpressBaseUrl) {
      setHeroArticle(getFallbackHeroArticle());
      setIsLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    const loadHeroPost = async () => {
      try {
        const queryParams = new URLSearchParams({
          _embed: "1",
          status: "publish",
          per_page: "100",
          orderby: "date",
          order: "desc",
        });

        const response = await fetch(`${wordpressBaseUrl}/posts?${queryParams.toString()}`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`WordPress request failed: ${response.status}`);
        }

        const data = await response.json();
        const publishedPosts = Array.isArray(data) ? data.filter((post) => post?.status === "publish") : [];
        const heroEligiblePosts = publishedPosts.filter(isHeroEligiblePost);
        const selectedPost = selectDailyPost(heroEligiblePosts);

        if (selectedPost) {
          const mappedHeroPost = mapHeroPost(selectedPost);

          setHeroArticle(mappedHeroPost);
          writeHeroCache(mappedHeroPost);
        } else {
          setHeroArticle(getFallbackHeroArticle());
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          setHeroArticle(getFallbackHeroArticle());
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadHeroPost();

    return () => controller.abort();
  }, [cachedHeroArticle]);

  const renderSkeleton = () => (
    <article
      id="featured-post"
      className="overflow-hidden rounded-[28px] border border-[rgba(32,42,96,0.1)] bg-white shadow-[0_28px_60px_rgba(25,18,12,0.12)]"
    >
      <div className="relative h-[210px] overflow-hidden bg-[#f3e7d0] sm:h-[220px] lg:h-[230px]">
        <div className="h-full w-full animate-pulse bg-gradient-to-br from-[#f7e8bc] via-[#f1d88a] to-[#faf2d6]" />
      </div>

      <div className="bg-white px-5 pb-5 pt-4 sm:px-6 sm:pb-6 sm:pt-5 lg:px-7 lg:pb-7 lg:pt-6">
        <div className="flex flex-wrap items-center gap-3 text-[12px] sm:text-[13px]">
          <div className="h-6 w-24 animate-pulse rounded-full bg-[#eee7d6]" />
        </div>

        <div className="mt-3 h-7 w-full animate-pulse rounded-full bg-[#eee7d6] sm:h-8 lg:h-9" />
        <div className="mt-3 h-7 w-11/12 animate-pulse rounded-full bg-[#eee7d6] sm:h-8 lg:h-9" />
        <div className="mt-3 h-7 w-10/12 animate-pulse rounded-full bg-[#eee7d6] sm:h-8 lg:h-9" />

        <div className="mt-5 flex items-center justify-between gap-3 border-t border-[rgba(32,42,96,0.08)] pt-4 sm:mt-2 sm:pt-2">
          <div className="h-4 w-24 animate-pulse rounded-full bg-[#eee7d6]" />
        </div>
      </div>
    </article>
  );

  return (
    <section id="blog-top" className="overflow-hidden bg-[--st-color-cream] px-4 pb-10 pt-6 sm:px-6 sm:pb-14 sm:pt-8 lg:px-8 lg:pb-16 lg:pt-10">
      <div className="mx-auto w-full max-w-7xl">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-stretch lg:gap-8">
          <div className="flex w-full flex-col justify-center lg:max-w-[44%] lg:pt-10">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-[var(--st-color-red)] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white sm:text-[12px]">
              <span className="text-[10px] leading-none text-[var(--st-color-cream)]">✦</span>
              Info &amp; Inspirasi
            </span>

            <h1 className="st-font-display mt-6 max-w-[14ch] text-[48px] leading-[0.95] tracking-[-0.04em] text-[var(--st-color-heading)] sm:text-[56px] md:text-[64px] lg:text-[76px]">
              Baca, Belajar,
              <br />
              <span className="text-[--st-color-red]">
                Raih <span className="font-semibold italic">Beasiswa!</span>
              </span>
              <br />
            </h1>

            <p className="mt-6 max-w-[620px] text-[15px] leading-[1.75] text-[var(--st-color-text-muted)] sm:text-[16px] lg:text-[18px]">
              Tips IELTS, info beasiswa terbaru, dan cerita sukses dari ribuan pelajar Indonesia yang sudah berhasil.
            </p>

            <div className="mt-8 flex flex-wrap gap-3 sm:mt-10">
              <button
                type="button"
                onClick={() => scrollToSection("blog")}
                className="inline-flex items-center justify-center rounded-full bg-[var(--st-color-red)] px-6 py-3.5 sm:px-9 sm:py-3.5 text-lg font-semibold text-[var(--st-color-cream)] shadow-[0_16px_28px_rgba(193,52,22,0.18)] transition-colors duration-200 hover:bg-[#a53317] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--st-color-navy)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--st-color-cream)]"
              >
                Explore Article
              </button>

              <button
                type="button"
                onClick={() => scrollToSection("newsletter")}
                className="inline-flex items-center justify-center rounded-full border border-[rgba(32,42,96,0.18)] bg-[var(--st-color-cream)] px-6 py-3.5 sm:px-9 sm:py-3.5 text-lg font-semibold text-[var(--st-color-red)] shadow-[0_10px_20px_rgba(35,28,22,0.06)] transition-colors duration-200 hover:border-[rgba(32,42,96,0.3)] hover:bg-[var(--st-color-surface-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--st-color-navy)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--st-color-cream)]"
              >
                Join Newsletter
              </button>
            </div>
          </div>

          <div className="w-full lg:flex-1">
            {isLoading ? (
              renderSkeleton()
            ) : (
              <a
                id="featured-post"
                href={heroArticle.href}
                className="group block overflow-hidden rounded-[28px] border border-[rgba(32,42,96,0.1)] bg-white shadow-[0_28px_60px_rgba(25,18,12,0.12)] transition-all duration-300 hover:border-[var(--st-color-red)] hover:shadow-[0_32px_70px_rgba(193,52,22,0.16)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--st-color-red)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--st-color-cream)]"
                aria-label={`Buka post: ${heroArticle.title}`}
              >
                <div className="relative h-[210px] overflow-hidden bg-[#f3e7d0] sm:h-[220px] lg:h-[230px]">
                  <img
                    src={heroArticle.image}
                    alt={heroArticle.title}
                    className="h-full w-full object-cover object-center transition-transform duration-300 group-hover:scale-[1.03]"
                    loading="eager"
                  />
                </div>

                <div className="bg-white px-5 pb-5 pt-4 sm:px-6 sm:pb-6 sm:pt-5 lg:px-7 lg:pb-7 lg:pt-6">
                  <div className="flex flex-wrap items-center gap-3 text-[12px] sm:text-[13px]">
                    <span className="inline-flex items-center rounded-full bg-[var(--st-color-red)] px-3 py-1 font-semibold uppercase tracking-[0.16em] text-white shadow-[0_10px_22px_rgba(193,52,22,0.18)]">
                      <span className="mr-2 text-[10px] leading-none">🔥</span>
                      Trending
                    </span>
                  </div>

                  <h2
                    className="mt-3 w-full overflow-hidden text-balance text-[24px] font-semibold leading-[1.20] tracking-[-0.035em] text-[var(--st-color-heading)] transition-colors duration-300 group-hover:text-[var(--st-color-red)] sm:text-[26px] lg:text-[28px]"
                    style={{
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 3,
                    }}
                  >
                    {heroArticle.title}
                  </h2>

                  <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-[rgba(32,42,96,0.08)] pt-4 sm:mt-2 sm:pt-2">
                    <div className="text-right text-[12px] font-medium text-[var(--st-color-text-muted)] sm:text-[13px]">
                      <span>{heroArticle.readTime}</span>
                      <span className="mx-2">·</span>
                      <span>{heroArticle.viewCount}</span>
                    </div>
                  </div>
                </div>
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroBlog;